import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Warehouse, Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { fetchStorageRates, saveStorageRates } from "../lib/api";
import { useBranding } from "../lib/company";

const exGst = (incl, factor) => {
  const n = parseFloat(incl);
  return Number.isFinite(n) ? (n / factor).toFixed(2) : "—";
};

/**
 * Storage Rates (Settings → admin) — party-negotiated daily storage rates.
 * Prices are entered INCL 15% GST (as negotiated with each party); quotes
 * bill the ex-GST equivalent so Xero adds GST once. Rates apply LIVE:
 * editing a price instantly recalculates every vehicle currently accruing.
 *
 * Auto-detection: AA/First Rescue jobs match by feed source (exact);
 * the others match editable keywords against program/customer/notes;
 * no match → impound default rate + "confirm rate" flag on the yard page.
 */
// Rates read as money, not as whatever the database happens to hold: 13.8
// is the same number as 13.80, but only one of them looks like a price.
const _row = (c) => ({
  ...c,
  rate_incl_gst:
    c.rate_incl_gst === null || c.rate_incl_gst === undefined || c.rate_incl_gst === ""
      ? ""
      : Number(c.rate_incl_gst).toFixed(2),
  keywordsText: (c.keywords || []).join(", "),
});

export const StorageRatesCard = () => {
  const branding = useBranding();
  const taxRate = Number(branding?.tax_rate ?? 0.15);
  const taxLabel = branding?.tax_label || "GST";
  const gstFactor = 1 + taxRate;
  const [cats, setCats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchStorageRates()
      .then((r) => setCats((r.categories || []).map(_row)))
      .catch(() => toast.error("Couldn't load storage rates"));
  }, []);

  const patchRow = (i, field, value) => {
    setCats((cur) => cur.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)));
  };

  const addRow = () => {
    setCats((cur) => [...cur, { key: null, label: "", rate_incl_gst: "", keywordsText: "", builtin: false }]);
  };

  const removeRow = (i) => {
    setCats((cur) => cur.filter((_, idx) => idx !== i));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = cats.map((c) => ({
        key: c.key || null,
        label: (c.label || "").trim(),
        rate_incl_gst: parseFloat(c.rate_incl_gst),
        keywords: (c.keywordsText || "").split(",").map((k) => k.trim()).filter(Boolean),
        builtin: !!c.builtin,
      }));
      if (payload.some((c) => !c.label)) { toast.error("Every category needs a name"); return; }
      if (payload.some((c) => !Number.isFinite(c.rate_incl_gst) || c.rate_incl_gst < 0)) { toast.error("Check the rates — one is not a valid price"); return; }
      const res = await saveStorageRates(payload);
      setCats((res.categories || []).map(_row));
      toast.success("Storage rates saved — all yard totals recalculated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save rates");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5" data-testid="storage-rates-card">
      <div className="flex items-center gap-2 mb-1">
        <Warehouse className="h-5 w-5 text-emerald-600" />
        <h2 className="font-display text-lg font-black tracking-tight text-slate-900 dark:text-slate-100">Storage Rates</h2>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        Negotiated per-day yard storage rates, entered <strong>incl. {+(taxRate * 100).toFixed(2)}% {taxLabel}</strong> — invoices bill the ex-{taxLabel}
        equivalent shown alongside. Rates apply live: changing a price recalculates every vehicle currently in the yard.
        AA / First Rescue jobs are matched automatically by feed source; the rest match the keyword lists against the
        job&apos;s program, customer name and notes. Anything unmatched uses the impound default rate and gets a
        &ldquo;confirm rate&rdquo; flag on the {branding?.salvage_entity_name || "Salvage"} Yards page.
      </p>

      {!cats ? (
        <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-2">
          <div className="hidden sm:grid grid-cols-[1fr_110px_90px_2fr_32px] gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1">
            <span>Category</span>
            <span>$/day incl {taxLabel}</span>
            <span>ex-{taxLabel}</span>
            <span>Match keywords (comma-separated)</span>
            <span />
          </div>
          {cats.map((c, i) => (
            <div key={c.key || `new-${i}`} className="grid grid-cols-2 sm:grid-cols-[1fr_110px_90px_2fr_32px] gap-2 items-center" data-testid={`storage-rate-row-${c.key || i}`}>
              <Input
                value={c.label}
                onChange={(e) => patchRow(i, "label", e.target.value)}
                placeholder="e.g. Pauls Panelbeaters"
                className="h-8 text-sm"
                data-testid={`storage-rate-label-${c.key || i}`}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={c.rate_incl_gst}
                onChange={(e) => patchRow(i, "rate_incl_gst", e.target.value)}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  if (e.target.value !== "" && Number.isFinite(n)) {
                    patchRow(i, "rate_incl_gst", n.toFixed(2));
                  }
                }}
                className="h-8 text-sm text-right tabular-nums"
                data-testid={`storage-rate-price-${c.key || i}`}
              />
              <span className="text-xs font-mono text-slate-500 dark:text-slate-400 text-right">${exGst(c.rate_incl_gst, gstFactor)}</span>
              <Input
                value={c.keywordsText}
                onChange={(e) => patchRow(i, "keywordsText", e.target.value)}
                placeholder="keywords, comma, separated"
                className="h-8 text-xs font-mono col-span-2 sm:col-span-1"
                data-testid={`storage-rate-keywords-${c.key || i}`}
              />
              {c.builtin ? (
                <span className="text-[9px] font-bold uppercase text-slate-400 text-center" title="Built-in category — can be re-priced but not deleted">core</span>
              ) : (
                <button
                  onClick={() => removeRow(i)}
                  className="text-slate-400 hover:text-rose-600 p-1.5 rounded"
                  title="Remove this category"
                  data-testid={`storage-rate-remove-${c.key || i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between pt-2">
            <Button variant="outline" size="sm" onClick={addRow} data-testid="storage-rate-add-btn">
              <Plus className="h-4 w-4" /> Add category
            </Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="storage-rate-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save rates
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorageRatesCard;
